import React, { useCallback, useEffect, useMemo } from "react";
import styled from "styled-components";

import Modal from "features/shared/components/modal";
import ModalHeader from "features/shared/components/modals/modalHeader";
import RawDataForm from "../form/rawDataForm";
import { useDispatch, useSelector } from "services/redux/rootReducer";
import { EditorAction } from "../../types/actions.editor";
import { ModalName } from "../../types/modal";
import { ImportExportType } from "../../types/importExport";

interface Props<T> {
    form: React.FC<{
        onSubmitCallback: (fields: T) => void;
        submitBtnText: string;
        originalFields?: T;
        readOnlyMode?: boolean;
        initialForm: T;
    }>;
    initialForm: T;
    onSubmitCallback?: (data: string | File | T) => void;
}

const GenericModal = <T extends Record<string, any>>({
    form: Form,
    initialForm,
    onSubmitCallback,
}: Props<T>) => {
    const dispatch = useDispatch();
    const { modal, modalConfig, importExportConfig } = useSelector(
        (state) => state.editor
    );

    const handleClose = useCallback(() => {
        dispatch({
            type: EditorAction.TOGGLE_MODAL,
        });
    }, [dispatch]);

    const handleSubmit = (submittedData: string | File | T) => {
        if (onSubmitCallback) {
            onSubmitCallback(submittedData);
            handleClose();
        }
    };

    const headingText = (() => {
        if (modal?.name !== ModalName.ADD) return modalConfig?.headingText;

        switch (importExportConfig.importType) {
            case ImportExportType.EXCEL:
                return "Import Excel Data";
            case ImportExportType.JSON:
                return "Import JSON Data";
            default:
                return modalConfig?.headingText;
        }
    })();

    const originalFields = useMemo(() => {
        if (
            modal?.name &&
            [ModalName.UPDATE, ModalName.VIEW].includes(modal?.name)
        ) {
            return modal?.params?.originalFields;
        }
        return undefined;
    }, [modal?.name, modal?.params?.originalFields]);

    const handleNonSubmitClose = useCallback(() => {
        if (window.confirm("Are you sure you want to exit?")) handleClose();
    }, [handleClose]);

    const handleKeyMovement = useCallback(
        (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                handleNonSubmitClose();
            }
        },
        [handleNonSubmitClose]
    );

    useEffect(() => {
        document.addEventListener("keydown", handleKeyMovement);
        return () => {
            document.removeEventListener("keydown", handleKeyMovement);
        };
    }, [handleKeyMovement]);

    return (
        <Modal id="genericModal" onCloseHandler={handleNonSubmitClose}>
            <Container>
                <ModalHeader
                    headingText={headingText || ""}
                    onCloseHandler={handleNonSubmitClose}
                />
                {[ImportExportType.EXCEL, ImportExportType.JSON].includes(
                    importExportConfig.importType
                ) && modal?.name === ModalName.ADD ? (
                    <RawDataForm onSubmitCallback={handleSubmit} />
                ) : (
                    <Form
                        onSubmitCallback={handleSubmit}
                        submitBtnText={modalConfig?.submitBtnText || ""}
                        originalFields={originalFields}
                        readOnlyMode={modal?.name === ModalName.VIEW}
                        initialForm={initialForm}
                    />
                )}
            </Container>
        </Modal>
    );
};

const Container = styled.div`
    background: white;
    color: ${(props) => props.theme.colors.neutral[800]};
    max-height: 95vh;
    width: 80vw;

    border-radius: 0.2rem;

    resize: both;
    overflow: auto;

    display: flex;
    flex-flow: column;

    & > *:first-child {
        flex: 0 0 4rem;
    }

    & > *:nth-child(2) {
        flex: 1;
    }

    @media (max-width: 768px) {
        width: 95vw;
        height: fit-content;
    }
`;

export default GenericModal;
